import { AppRoutes } from 'src/lib/routes';
import { NextSeo } from 'next-seo';
import WithDefaultJsonLd from 'src/components/WithDefaultJsonLd';
import ImageWithFallback from 'src/components/ImageWithFallback';
import Sustainability1Image from 'public/images/about/sustainability_1.png';
import Sustainability2Image from 'public/images/about/sustainability_2.png';
import Sustainability3Image from 'public/images/about/sustainability_3.png';
import Sustainability4Image from 'public/images/about/sustainability_4.png';
import SustainabilityImage from 'public/images/about/our-sustainability.png';
import OurMissionImage from 'public/images/home/our_mission.jpg';
import styles from 'src/styles/modules/pages/about.module.scss';
import imageStyles from 'src/styles/modules/components/image.module.scss';
import Link from 'next/link';
import classNames from 'classnames';
import AccordionFaq from 'src/components/AccordionFaq';
import AboutListCards from 'src/components/AboutListCards';
import { Tips } from 'src/components/Tips';

const metaTitle = 'Sustainability 101';
const metaDesc =
  'Seek Sophie is founded by travellers, for travellers. A travel platform that makes adventurous trips super simple to book, based on your travel preferences';

const faqs = [
  {
    title: 'Is sustainable travel always more expensive?',
    content: (
      <>
        <div className="tw-mb-2">
          Sustainable travel doesn’t have to be pricey. Sometimes it costs a bit more than big bus
          tours because it’s in smaller groups and goes to spots away from the crowds—so costs
          aren’t spread across as many people. But you get a unique experience without all the
          touristy stuff.
        </div>
        <div className="tw-mb-2">
          However, a big reason prices can be high is the layers of middlemen in traditional travel
          chains. At Seek Sophie, we skip these extra layers by connecting you directly with local
          guides. This keeps costs low for you and ensures your money goes straight to the local
          community.
        </div>
        <div>
          Seek Sophie experiences start as low as $10, and you can travel knowing you{"'"}
          re supporting local guides that care for their environment and culture.
        </div>
      </>
    ),
  },
  {
    title: 'How can I travel more sustainably?',
    content: (
      <>
        <div className="tw-mb-2">
          Travelling more sustainably is about being mindful of the choices we make. Every choice
          has an impact—good or bad. By doing our research and being thoughtful, we can make travel
          a force for good.
        </div>
        <div className="tw-mb-2">
          For example, you can choose smaller, local-led tours, avoid overtouristed spots, and
          support eco-friendly accommodations. Opt for slower travel, reduce plastic waste, and
          respect local cultures and wildlife.
        </div>
        <div className="tw-mb-2">
          Keep in mind that sustainability looks different in each place. What seems like poor
          practices, like visible trash, might be caused by factors beyond local control, such as
          ocean currents bringing waste from elsewhere.
        </div>
        <div>
          It’s easy to judge or feel discouraged, but it’s amazing for us to see that many local
          organisations are making a real difference. As travellers, we can support them by staying
          informed, thoughtful, and open-minded. Every mindful choice makes a difference.
        </div>
      </>
    ),
  },
  {
    title: 'What’s overtourism and why is it bad?',
    content: (
      <>
        <div className="tw-mb-2">
          Overtourism is where the number of travellers to a certain location overwhelms the place,
          and makes the local communities and the environment worse off.
        </div>
        <div className="tw-mb-2">
          It causes environmental degradation, cultural erosion, and strain on infrastructure,
          making it difficult for local residents to maintain their quality of life.
        </div>
        <div className="tw-mb-2">
          On the flip side, there’s a lesser-known issue called undertourism, which is just as
          problematic. In many parts of Asia, there are prehistoric rainforests, ancient volcanoes
          and areas full of wildlife that no one knows about, and no one goes to. These incredible
          natural wonders are in danger of being converted into palm oil plantations or coal mines
          due to a lack of tourism income. Without tourism, local residents also leave, which
          creates a brain drain and diminishes local culture.
        </div>
        <div className="tw-mb-2">
          Both overtourism and undertourism are problematic, and at Seek Sophie, we strive to find a
          balance. In areas facing a surge of tourists, we seek out alternative experiences that
          allow travellers to enjoy the beauty away from the crowds while supporting local
          communities. For destinations with undertourism, we tell their incredible stories to
          travellers, so travel spending can help sustain and preserve these places.
        </div>
        <div>
          Just like any delicate ecosystem, finding balance is essential. If we want our travels to
          be a force for good, we should seek to avoid overtouristed areas and explore the untouched
          beauty the world has to offer.
        </div>
      </>
    ),
  },
  {
    title: 'How can I find local experiences that aren’t tourist traps?',
    content: (
      <>
        <div className="tw-mb-8">
          That’s why Seek Sophie exists :) As travellers, we know the struggle of searching to the
          ends of Google for experiences that aren’t tourist traps—only to sometimes end up in one
          anyway. That’s why we go on the ground, do our homework and try out the experiences
          ourselves to make sure what we recommend are what’s worth your time.
        </div>
        <div className="tw-mb-4">Here are a few tips to help you find the good good stuff:</div>
        <div>
          <ul className="tw-list-decimal tw-font-semibold tw-pl-5">
            <li className="tw-mb-2">
              Ask Locals, But Be Careful:
              <span className="tw-font-normal tw-ml-1">
                In many developing areas of Asia, locals might not always know what travellers are
                looking for. They can be hesitant to recommend truly local spots, worrying that they
                might not be clean or too basic for visitors. So, while asking locals can sometimes
                yield great finds, it’s not a guaranteed win.
              </span>
            </li>
            <li className="tw-mb-2">
              Social Media Alert:
              <span className="tw-font-normal tw-ml-1">
                If a place is blowing up on social media, it’s likely overtouristed. Those
                ‘must-see’ spots can often become overcrowded and lose their charm.
              </span>
            </li>
            <li>
              Tap Into Local Guides:
              <span className="tw-font-normal tw-ml-1">
                We always take a guide when we travel - because that’s how you find the truly good
                stuff that aren’t on Google or social media! The best local guides usually know some
                hidden gems that’ll give you a richer experience of the local culture.
              </span>
            </li>
          </ul>
        </div>
      </>
    ),
  },
  {
    title: 'How do I ensure my money supports locals?',
    content: (
      <>
        <div className="tw-mb-2">
          A good rule of thumb is that the smaller the business, the more likely your money is going
          directly to locals. Supporting local mom-and-pop businesses can really make a difference
          for the community.
        </div>
        <div className="tw-mb-2">
          On the flip side, large attractions and hotels are often owned by corporations or foreign
          entities, which can limit the economic benefits for local communities. While they do
          provide jobs for locals, a smaller portion of your spending actually goes back to the
          community.
        </div>
        <div>
          So if you’d like to support local when you travel, consider taking local guides, staying
          at small family-run or locally owned hotels, and dining at local eateries. Every mindful
          choice helps!
        </div>
      </>
    ),
  },
  {
    title: 'Is that animal experience I’m eyeing ethical, or should I think twice?',
    content: (
      <>
        <div className="tw-mb-2">
          A simple rule of thumb is that if there’s any feeding, animal performance, or close
          interaction with the wildlife, you should think twice.
        </div>
        <div className="tw-mb-2">
          When considering an animal experience, it’s crucial to ask whether it genuinely respects
          wildlife and their natural behaviors. At Seek Sophie, we believe that wildlife should live
          as freely as possible, with minimal human interaction. Unfortunately, many attractions
          capture wild animals for tourism, leading to mistreatment and suffering.
        </div>
        <div className="tw-mb-2">To check if an experience is ethical, consider these points:</div>
        <ul className="tw-pl-5 tw-font-semibold tw-mb-2">
          <li className="tw-mb-2">
            Is it a Natural Habitat:
            <span className="tw-font-normal tw-ml-1">
              Ethical experiences should involve wildlife in their natural environments, like
              wildlife walks or safaris that respect animals’ space.
            </span>
          </li>
          <li className="tw-mb-2">
            Are the Welfare Standards Good:
            <span className="tw-font-normal tw-ml-1">
              If it’s a sanctuary, check that it prioritises animal welfare. Look for places that
              provide ample space, natural food, and minimal forced interaction. Avoid places that
              resemble petting zoos.
            </span>
          </li>
          <li className="tw-mb-2">
            No Stressful Interactions:
            <span className="tw-font-normal tw-ml-1">
              Ethical experiences never encourage close encounters that could stress animals. This
              includes avoiding elephant rides, big cat petting, or selfies with wild animals.
            </span>
          </li>
          <li className="tw-mb-2">
            No Performances:
            <span className="tw-font-normal tw-ml-1">
              Be wary of attractions where animals perform, as this often involves cruel training
              methods.
            </span>
          </li>
          <li className="tw-mb-2">
            Conservation Impact:
            <span className="tw-font-normal tw-ml-1">
              Look for experiences that support wildlife conservation and local communities.
            </span>
          </li>
        </ul>
        <div>
          If you’re unsure, do your research and seek recommendations from local conservationists.
          At Seek Sophie, we’re dedicated to offering experiences that align with these values,
          ensuring you can enjoy wildlife encounters that are respectful and beneficial.
        </div>
      </>
    ),
  },
  {
    title: 'Is flying always a no-go for the planet? ',
    content: (
      <>
        <div className="tw-mb-2">
          From an emissions perspective, commercial flights is actually less harmful for the planet
          than fast fashion and agriculture. It’s not great by any means, but it’s not as terrible
          as many make it out to be. The reality is that most travellers will continue to fly and
          are likely to do so even more in the future.
        </div>
        <div className="tw-mb-2">
          So what can we do about the emissions from flying? Our choices do matter here. We can
          choose carriers that use green fuels, we can choose more short haul trips and we can also
          choose to stay longer in places we’re in.
        </div>
        <div>
          Another point to consider is carbon offsets. Some view these as greenwashing, but at Seek
          Sophie, we believe they have to be part of the solution{' '}
          <Link legacyBehavior prefetch={false} href={AppRoutes.ourCarbonCommitmentRoute().toPath()}>
            <a className="tw-text-brand-navy tw-underline">(more here)</a>
          </Link>
          . Many carbon sinks rely on these programs for protection and won’t exist without offset
          programs. If we’re serious about our carbon footprint—whether from flying, fashion, or
          food—carbon offsets should be included in our approach to sustainability.
        </div>
      </>
    ),
  },
  {
    title: 'How can I reduce my carbon footprint while traveling?',
    content: (
      <>
        <div className="tw-mb-2">
          <strong>When flying:</strong> There are plenty of ways to reduce your footprint while
          traveling! When you fly, opt for airlines that use green fuels, choose more short-haul
          trips, and consider extending your stays in places you visit. Donating to carbon offset
          programs is another great option.
        </div>
        <div className="tw-mb-2">
          <strong>Planning activities:</strong> When planning activities, skip the fancy brunch and
          look for farm-to-table experiences. This way, you’re supporting local farms and enjoying
          fresh food that hasn’t traveled from across the globe. Instead of visiting large
          attractions, try local activities like cycling or walking tours. These not only have a
          lower footprint but also help you connect with the community.
        </div>
        <div>
          <strong>Choosing accommodation:</strong> When it comes to accommodations, favour
          eco-friendly or locally owned options over large, faceless hotels. This supports the
          community and often reduces your carbon footprint. Plus, these places tend to have more
          character and can enhance your connection to the area, making your stay even more
          enjoyable!
        </div>
      </>
    ),
  },
  {
    title: 'Can I trust all those “eco-friendly” travel companies, or should I be skeptical?',
    content: (
      <>
        <div className="tw-mb-2">
          That’s a great question! It’s totally understandable to feel skeptical about eco-friendly
          claims, especially with all the greenwashing out there. Some places that say they{"'"}re
          sustainable or ethical may not really be living up to it.
        </div>
        <div className="tw-mb-2">
          There’s no easy answer. It’s up to us as consumers to do our research and see what truly
          passes the “smell test.” Sometimes, you might have to dig deep into Google or sift through
          reviews to find that one comment that raises a red flag.
        </div>
        <div className="tw-mb-2">
          At Seek Sophie, we talk to conservationists, connect with local communities, and visit
          experiences ourselves to make sure they’re genuinely sustainable. We don’t always get it
          right, either, so we know how tricky it can be.
        </div>
        <div>
          But it’s encouraging to see more companies trying to do the right thing—that’s a big step
          for the travel industry. While we’re making progress, it’s essential for each of us to
          research the businesses we support when we travel. The choices we make when we travel -
          for better or for worse - will shape the kind of world we live in, for us and for future
          generations.
        </div>
      </>
    ),
  },
  {
    title:
      'How do I stay motivated to travel sustainably, even when it’s not the cheapest or easiest choice?',
    content: (
      <>
        <div className="tw-mb-2">
          Great question! Staying motivated to travel sustainably can be challenging, especially
          since it often requires a lot of research and mindfulness.
        </div>
        <div className="tw-mb-2">
          First, don’t stress too much! Feeling overwhelmed can make it harder to stay motivated.
          Just do your best! If you can research your experiences to see if they’re overtouristed or
          harmful, that{"'"}s a win. Spending one day with a local guide during your week-long trip?
          Amazing! Choosing to stay in a locally-owned place instead of a chain hotel? Fantastic!
        </div>
        <div>
          Remember, every choice counts. Making just one mindful decision—like supporting local
          communities or avoiding harmful activities—makes a significant impact. So celebrate the
          small wins, and know that even one mindful choice on every trip can make a difference!
        </div>
      </>
    ),
  },
];

const items = [
  {
    title: 'Better for Travellers',
    body: 'We do all the homework so you don’t have to. We only list things on Seek Sophie that we believe are worthwhile, not tourist traps. What we show you isn’t sponsored, isn’t sorted based on revenue, and is our honest opinion.',
    image: Sustainability1Image,
    url: AppRoutes.betterTravellersRoute().toUrl(),
  },
  {
    title: 'Better for Local Communities',
    body: 'We do our best to make sure that the money you spend stays in the local community. We find responsible local guides and businesses who are making a difference in their communities.',
    image: Sustainability2Image,
    url: AppRoutes.betterLocalCommunitiesRoute().toUrl(),
  },
  {
    title: 'Better for the Environment',
    body: 'We do our research and only choose experiences and guides that are respectful of the environment they’re in. We want to be part of the solution, not the problem. ',
    image: Sustainability3Image,
    url: AppRoutes.betterEnvironmentRoute().toUrl(),
  },
  {
    title: 'Our Net-Zero Pledge',
    body: 'We offset the carbon footprint of every experience through planting trees, and protecting carbon-sequestering ecosystems such as mangrove forests. It’s not perfect, but we believe it needs to be part of the solution.',
    image: Sustainability4Image,
    url: AppRoutes.ourCarbonCommitmentRoute().toUrl(),
  },
];

const tips = [
  {
    title: 'Take a local guide',
    tipCardColor: 'tw-bg-orange-light',
    content: (
      <>
        <div className="tw-mb-2">
          This is our #1 tip for travelling! Hiring a local guide is a win-win.
        </div>
        <div className="tw-mb-2">
          The best local guides know the hidden gems and secret routes. They’re your secret weapon
          to making your experience more authentic and helping you feel connected to the place.
        </div>
        <div>Plus, your travel spending will directly benefit the local community.</div>
      </>
    ),
  },
  {
    title: 'Go on a less touristed route or off-peak time',
    tipCardColor: 'tw-bg-purple',
    content: (
      <>
        <div className="tw-mb-2">
          When travellers all go to the same place at the same time, it’s not only a terrible travel
          experience, it also makes the local communities and the environment worse off.
        </div>
        <div>
          So when you choose a less-touristed route or go at an off-peak time, you’re actually
          helping to reduce the strain on the place and you also get to explore in a more meaningful
          way!
        </div>
      </>
    ),
  },
  {
    title: 'Choose ethical wildlife experiences',
    tipCardColor: 'tw-bg-brand-green',
    content: (
      <>
        <div className="tw-mb-2">
          Many wildlife experiences marketed as ethical aren’t actually so. It’s important to do
          your research! If you see any feeding, animal performances, or close interactions with the
          wildlife, it’s a red flag.
        </div>
        <div>
          Unethical experiences can be cruel and disrupt natural wildlife behaviours, making animals
          more vulnerable to poaching and less capable of surviving in the wild.
        </div>
      </>
    ),
  },
  {
    title: 'Support Small Local Businesses',
    tipCardColor: 'tw-bg-brand-yellow',
    content: (
      <>
        <div className="tw-mb-2">
          Support local businesses by dining at independent restaurants, having locally sourced
          food, staying in small locally-owned accommodations, and buying locally handmade goods.
        </div>
        <div>
          This way, your travel dollars go directly to the community instead of big corporations!
        </div>
      </>
    ),
  },
];

export async function getStaticProps() {
  return {
    props: {},
  };
}

const Page = () => {
  return (
    <>
      <NextSeo
        title={metaTitle}
        description={metaDesc}
        canonical={AppRoutes.ourMissionRoute().toUrl()}
        openGraph={{
          url: AppRoutes.ourMissionRoute().toUrl(),
          title: metaTitle,
          description: metaDesc,
        }}
      />
      <WithDefaultJsonLd />

      <div className={`${imageStyles.heroImgContainer}`}>
        <ImageWithFallback
          src={OurMissionImage}
          fill
          alt=""
          className={`tw-static tw-block tw-object-cover tw-w-full tw-h-full`}
        />

        <div
          className="tw-hidden md:tw-block tw-w-full tw-h-full tw-absolute tw-top-0 tw-left-0"
          style={{
            background:
              'linear-gradient(89deg, rgba(0, 0, 0, 0.90) -41.61%, rgba(0, 0, 0, 0.00) 99.18%);',
          }}
        ></div>

        <div className="tw-left-0 tw-absolute tw-z-10 tw-top-20 md:tw-top-24 tw-w-full">
          <div className="container">
            <div className="tw-text-center tw-font-semibold tw-leading-none tw-text-6xl md:tw-text-8xl tw-font-styleSans tw-text-white tw-mb-8 tw-px-12">
              Sustainability 101
            </div>
          </div>
        </div>
      </div>

      <div className="tw-bg-brand-yellow">
        <div className="container tw-max-w-2xl tw-py-16 md:tw-py-24 tw-text-center">
          <div className="tw-relative tw-mb-8">
            <div className="tw-relative tw-font-semibold tw-text-5xl md:tw-text-6xl tw-leading-none md:tw-leading-none tw-z-10 tw-font-styleSans md:tw-max-w-lg tw-px-8 tw-text-center tw-mx-auto">
              What is Sustainable Travel?
            </div>
          </div>

          <div className="md:tw-text-lg">
            <div className="tw-mb-6 md:tw-mb-8">
              There’s no single definition of sustainable travel, but to us it’s simple: It means
              leaving places better than we found it. When our travels benefit locals and protect
              nature, future generations can enjoy these amazing places too.
            </div>
            <div className="tw-mb-6 md:tw-mb-8">
              For us, sustainable travel is about making sure our adventures benefit everyone
              involved—<strong>travellers</strong>, <strong>locals</strong>, and{' '}
              <strong>the environment</strong>. Our ecosystem is interconnected, and when each part
              of our ecosystem thrives, it leads to a better experience for all.
            </div>
          </div>
        </div>

        <div className="tw-bg-brand-green">
          <div className="container tw-max-w-4xl md:tw-py-24 tw-py-16">
            <div className="md:tw-flex tw-flex-row tw-justify-center">
              <div className="tw-text-center tw-mb-8 tw-text-5xl tw-font-styleSans tw-font-semibold tw-block md:tw-hidden tw-w-full">
                Why does it matter?
              </div>
              <div className="md:tw-mr-16 tw-mr-0">
                <ImageWithFallback
                  src={SustainabilityImage}
                  fill
                  alt=""
                  className="tw-static tw-block tw-object-cover md:tw-w-auto md:tw-h-96 tw-h-auto tw-w-full"
                />
              </div>
              <div className="tw-flex tw-justify-center tw-items-center">
                <div className="tw-text-center">
                  <div className="tw-text-4xl tw-font-styleSans tw-font-semibold md:tw-block tw-hidden">
                    Why does it matter?
                  </div>
                  <div className="tw-mt-8">
                    Indigenous cultures, wildlife, and natural landscapes are vanishing faster than
                    ever, and it can feel overwhelming. But at Seek Sophie, we believe that every
                    one of us can be part of the solution! By travelling mindfully, we can help
                    preserve rich cultures, protect endangered wildlife, and uplift local
                    communities. Each trip is a chance to make a real impact, ensuring these
                    incredible places and cultures thrive for generations to come!
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="tw-bg-white md:tw-py-24 tw-py-16">
          <div className="container tw-max-w-2xl">
            <div className="tw-mb-16">
              <div className="tw-font-semibold tw-text-5xl md:tw-text-6xl tw-leading-none md:tw-leading-none tw-z-10 tw-font-styleSans md:tw-max-w-lg tw-text-center tw-mx-auto">
                Travel as a force for good
              </div>
            </div>
            {items.map((item, index) => (
              <div
                key={index}
                className={`tw-flex tw-flex-col md:tw-flex-row md:tw-items-center tw-w-full ${
                  index > 0 ? 'tw-mt-16 md:tw-mt-24' : ''
                }`}
              >
                <div
                  className={classNames(
                    'tw-w-40 md:tw-w-44 tw-relative tw-flex-shrink-0 tw-mx-auto tw-mb-6 md:tw-mb-0',
                    { 'md:tw-pr-4': index === 1 || index === 3 }
                  )}
                >
                  <ImageWithFallback
                    src={item.image}
                    alt={item.title}
                    className="tw-w-full tw-h-auto"
                  />
                </div>
                <div className="md:tw-ml-12 tw-text-center md:tw-text-left">
                  <div className="tw-font-styleSans tw-font-semibold tw-text-3xl md:tw-text-4xl tw-leading-none md:tw-leading-none tw-mb-4 tw-px-8 md:tw-px-0">
                    {item.title}
                  </div>
                  <div className="tw-mb-4 md:tw-mb-8">{item.body}</div>
                  <div>
                    <Link legacyBehavior prefetch={false} href={item.url}>
                      <a className="btn btn-primary tw-px-8 tw-py-3 tw-rounded-4xl">learn more</a>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="tw-bg-grey-lightest md:tw-py-24 tw-py-16">
          <div className="container">
            <h2 className="tw-text-2xl md:tw-text-4xl tw-mb-4 md:tw-mb-6 tw-font-styleSans tw-font-semibold tw-text-brand-navy tw-text-center">
              Beginner’s Guide to Sustainable Travel
            </h2>

            <div id="faq" className="tw-max-w-3xl tw-mx-auto tw-pb-24">
              {faqs.map((faq, index) => (
                <AccordionFaq
                  key={index}
                  keyName={`faq-${index}`}
                  title={faq.title}
                  renderTitle={() => (
                    <span className="tw-font-semibold tw-font-styleSans md:tw-text-xl tw-text-lg tw-leading-none md:tw-leading-none">
                      {faq.title}
                    </span>
                  )}
                >
                  {faq.content}
                </AccordionFaq>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="tw-bg-brand-navy tw-py-24">
        <div className="container tw-px-0 md:tw-pl-0 tw-pl-6">
          <div className="tw-font-semibold tw-leading-none tw-text-5xl md:tw-text-6xl tw-font-styleSans tw-text-brand-green tw-mb-16 tw-text-center tw-max-w-xs md:tw-max-w-full tw-mx-auto">
            4 Simple Ways to
            <br /> Travel Sustainably
          </div>
          <Tips tips={tips} tipCardClass="md:tw-w-64 tw-w-72 tw-h-96 tw-mr-6 tw-rounded-lg" />
        </div>
      </div>
      <div className="container tw-max-w-4xl md:tw-py-24 tw-py-16">
        <AboutListCards currentPage="sustainability" />
      </div>
    </>
  );
};

export default Page;
